import { AllocationRule } from 'further-types/lp/allocations';
import { DrawdownSourcePriority } from 'further-types/lp/capital-action';
import { CalendarDay } from 'further-ui/utils';
import api from 'lib/trpcClient';
import { useEffect, useState } from 'react';

type CalledCapitalAllocation = {
  commitmentId: string;
  amountLcy: number;
  investorLabel: string;
  totalCommitmentLcy: number;
  unallocatedCommitmentLcy: number;
  uncalledCommitmentLcy: number;
  allocationPercentage: number;
  recallableDistributionLcy: number;
  availableToBeCalledLcy: number;
  excludeFromAllocation: boolean;
  closeDate: CalendarDay;
  fundsCalled: {
    fromCommitmentLcy: number;
    fromRecallableDistributionLcy: number;
  };
};

export default function useAllocateCapitalCall({
  vehicleId,
  actionId,
  onSuccess,
  allocationRule,
  excludedCommitmentIds,
  capitalCalledLcy,
  drawdownSourcePriority,
  recallableDistributionCalledInOtherEntries,
  existingBreakdown,
  isNewForm,
  onFirstLoadExclusionsSync,
}: {
  vehicleId: string;
  actionId?: string;
  onSuccess: (allocations: Array<CalledCapitalAllocation>) => void;
  allocationRule: AllocationRule;
  excludedCommitmentIds: Array<string>;
  capitalCalledLcy: number;
  drawdownSourcePriority: DrawdownSourcePriority;
  recallableDistributionCalledInOtherEntries?: Array<{
    commitmentId: string;
    calledAmountLcy: number;
  }>;
  existingBreakdown: Array<{
    commitmentId: string;
    fundsCalled?: {
      fromCommitmentLcy: number;
      fromRecallableDistributionLcy: number;
    };
  }>;
  isNewForm: boolean;
  onFirstLoadExclusionsSync: (ids: Array<string>) => void;
}) {
  const [calledCapitalAllocations, setCalledCapitalAllocation] = useState<
    Array<CalledCapitalAllocation>
  >([]);

  const hasAllocationsLoaded = !!calledCapitalAllocations.length;
  const applyVehicleExclusions = isNewForm && !hasAllocationsLoaded;

  const { mutate, isPending } =
    api.capitalAction.allocateCalledCapital.useMutation({
      onSuccess: (allocations) => {
        const unpaidAllocations = allocations.map((allocation) => ({
          ...allocation,
          amountLcy:
            allocation.unpaidAllocationAmountLcy ?? allocation.amountLcy,
        }));

        setCalledCapitalAllocation(unpaidAllocations);
        onSuccess(unpaidAllocations);

        if (applyVehicleExclusions) {
          const autoExcludedIds = unpaidAllocations
            .filter((a) => a.excludeFromAllocation)
            .map((a) => a.commitmentId);
          if (autoExcludedIds.length > 0) {
            onFirstLoadExclusionsSync(autoExcludedIds);
          }
        }
      },
    });

  const allocateCapitalCall = async ({
    useExistingBreakdown,
    drawdownSourcePriority: drawdownSourcePriorityOverride,
  }: {
    useExistingBreakdown: boolean;
    drawdownSourcePriority?: DrawdownSourcePriority;
  }) => {
    if (isPending) {
      return;
    }

    mutate({
      drawdownSourcePriority:
        drawdownSourcePriorityOverride ?? drawdownSourcePriority,
      allocationRule,
      capitalCalledLcy,
      excludedCommitmentIds,
      vehicleId,
      actionId,
      recallableDistributionCalledInOtherEntries:
        recallableDistributionCalledInOtherEntries ?? [],
      existingBreakdown:
        useExistingBreakdown && existingBreakdown ? existingBreakdown : [],
      applyVehicleExclusions,
    });
  };

  useEffect(() => {
    if (allocationRule === AllocationRule.Manual && hasAllocationsLoaded) {
      return;
    }

    allocateCapitalCall({
      useExistingBreakdown: !hasAllocationsLoaded,
    });
  }, [excludedCommitmentIds, capitalCalledLcy, allocationRule]);

  const hasRecallableDistribution = calledCapitalAllocations.some(
    (allocation) => allocation.recallableDistributionLcy > 0,
  );

  return {
    calledCapitalAllocations,
    isPending,
    hasRecallableDistribution,
    allocateCapitalCall,
  };
}
